import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import partial from 'lodash/partial';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import hooks from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';
import GAClickEventTracker from '../../GAClickEventTracker';
import { conditionallyRequired } from '../../../utils/propTypesUtils';

import Tab from './Tab/Tab';
import { Box, ConditionalWrap, Layout, useMedia } from '../../Boneyard';
import {
  NEXT,
  PREVIOUS,
  RETURNING,
} from '../../../pageComponents/indexPage/constants';

const propTypes = {
  // to be used when there's two or more Tabs components on the same "page"
  namespace: PropTypes.string,
  onChange: PropTypes.func,
  /** Allow keyboard navigation via the left and right arrow keys */
  allowKeyboardNavigation: PropTypes.bool,
  /** Accepts `Tab` children */
  children: (props, propName, componentName) => {
    const prop = props[propName];

    React.Children.forEach(prop, child => {
      if (child.type !== Tab) {
        return new Error(
          `${componentName} should have children of type '${Tab.displayName}' but got '${child.type}'`
        );
      }
    });
  },
  /** class name from styled components */
  className: PropTypes.string,
  /** Enables arrows navigation */
  withNavigationArrows: PropTypes.bool,
  defaultTab: PropTypes.number,
  tabsWrapperHook: PropTypes.string.isRequired,
  rightArrowAutomationHook: PropTypes.string,
  leftArrowAutomationHook: PropTypes.string,
  arrowsDisabledStatus: PropTypes.object,
  leftArrowAriaLabel: PropTypes.string,
  rightArrowAriaLabel: PropTypes.string,
  borderColor: PropTypes.string,
  leg: PropTypes.string,
  onArrowClick: conditionallyRequired(
    PropTypes.func,
    props => props.withNavigationArrows
  ),
  doNotShowBottomBorderUnderTabs: PropTypes.bool,
};

const defaultProps = {
  onChange: Function.prototype,
  allowKeyboardNavigation: true,
  withNavigationArrows: false,
  defaultTab: 0,
  leftArrowAriaLabel: 'previous',
  rightArrowAriaLabel: 'next',
  borderColor: '#cfd8dc',
  doNotShowBottomBorderUnderTabs: null,
};

const Tabs = props => {
  const [currentTabIndex, setCurrentTabIndex] = useState(props.defaultTab);
  const mq = useMedia();
  const isMobile = mq.lte('sm');

  useEffect(() => {
    setCurrentTabIndex(props.defaultTab);
  }, [props.defaultTab]);

  const [disabledTabs, setDisabledTabs] = useState([]);

  const tabCount = React.Children.count(props.children);

  // const tabPanelId = uniqueId('Tabs-panel-');
  const tabRefs = React.useRef([...Array(tabCount)].map(React.createRef))
    .current;

  const activateTab = useCallback(
    (index, child, donotClearWarning) => {
      setCurrentTabIndex(index);
      const onChange = props.onChange;

      if (child) {
        onChange(index, child.props, donotClearWarning);
      } else {
        onChange(index, donotClearWarning);
      }
      // programmatically set focus to the active tab
      if (tabRefs[index]) {
        tabRefs[index].current.focus();
      }
    },
    [props.onChange, tabRefs]
  );

  useEffect(() => {
    let disabledIndexes = [];
    React.Children.forEach(props.children, (child, index) => {
      if (child.props.disabled) {
        disabledIndexes = [...disabledIndexes, index];
      }
    });

    setDisabledTabs(disabledIndexes);

    // if the current tab is disabled but there are available tabs, focus on the first available tab
    const tabMap = React.Children.map(
      props.children,
      child => !child.props.disabled
    );

    const firstEnabledTab = tabMap.indexOf(true, props.defaultTab)
      ? tabMap.indexOf(true, props.defaultTab)
      : tabMap.indexOf(true);
    let donotClearWarning = false;
    if (props.defaultTab < firstEnabledTab && props.leg === RETURNING) {
      donotClearWarning = true;
    }
    if (!tabMap[props.defaultTab] && firstEnabledTab !== -1) {
      activateTab(
        firstEnabledTab,
        props.children[firstEnabledTab],
        donotClearWarning
      );
    }
  }, [
    props.children,
    currentTabIndex,
    props.defaultTab,
    activateTab,
    props.leg,
  ]);

  const onKeyDown = event => {
    if (props.allowKeyboardNavigation) {
      if (event.keyCode === KEY_LEFT_ARROW) {
        prev();
      } else if (event.keyCode === KEY_RIGHT_ARROW) {
        next();
      }
    }
  };

  const tabChildren = React.Children.map(props.children, child => child);

  const prev = () => {
    const tabCount = React.Children.count(props.children);
    const tabMap = new Array(tabCount)
      .fill(null)
      .map((value, index) => !disabledTabs.includes(index));
    const nextEnabledTabs = tabMap.slice(0, currentTabIndex);
    const lastAvailableIndex = findLastIndex(nextEnabledTabs, Boolean);

    const nextTabIndex =
      lastAvailableIndex !== -1 ? lastAvailableIndex : currentTabIndex;

    activateTab(nextTabIndex, tabChildren[nextTabIndex], false);
  };

  const next = () => {
    const tabCount = React.Children.count(props.children);
    const tabMap = new Array(tabCount)
      .fill(null)
      .map((value, index) => !disabledTabs.includes(index));
    const nextEnabledTabs = tabMap.slice(currentTabIndex + 1);

    const nextTabIndex = nextEnabledTabs.length
      ? currentTabIndex + findIndex(nextEnabledTabs, Boolean) + 1
      : currentTabIndex;

    activateTab(nextTabIndex, tabChildren[nextTabIndex], false);
  };
  const renderTabContents = () => {
    const activeChildren = props.children[currentTabIndex];

    if (activeChildren) {
      return activeChildren.props.children;
    }

    return null;
  };

  const makeFirstLetterUppercase = word => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  };

  const arrowClickTracker = nextOrPrevious => {
    tracking.trackEvent(
      'site_click_tracking',
      'Flight Chooser',
      `${makeFirstLetterUppercase(
        props.leg
      )} Flight | ${makeFirstLetterUppercase(nextOrPrevious)} Page`
    );
  };

  const handleLeftArrowClick = () => {
    props.onArrowClick(props.leg, PREVIOUS);
  };

  const handleRightArrowClick = () => {
    props.onArrowClick(props.leg, NEXT);
  };

  const defaultBottomBorderUnderTabs = useMemo(() => {
    if (!isMobile) {
      return `2px solid ${props.borderColor}`;
    }
  }, [isMobile, props.borderColor]);

  // media queries should not be checked in the render of a component level,
  // they should be passed down as props from root, or at least received through useMedia()
  // storing it in props allows mq to be a dependency in useCallback and useMemo
  // which allows more flexibility and optimization in the code
  // also reduces the number of times the code in the Media component needs to be run
  return (
    <ConditionalWrap
      condition={mq.lt('md')}
      wrap={children => <Layout.FullWidth children={children} />}
      children={
        <div className={props.className}>
          <div onKeyDown={onKeyDown}>
            <Box
              d="flex"
              justifyContent="space-between"
              bb={
                props.doNotShowBottomBorderUnderTabs === null
                  ? defaultBottomBorderUnderTabs
                  : props.doNotShowBottomBorderUnderTabs
                  ? null
                  : defaultBottomBorderUnderTabs
              }
            >
              {props.withNavigationArrows && (
                <Box
                  w="5%"
                  d="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <GAClickEventTracker
                    onClick={() => arrowClickTracker(props.leftArrowAriaLabel)}
                  >
                    <ArrowButton
                      type="button"
                      aria-label={props.leftArrowAriaLabel}
                      disabled={props.arrowsDisabledStatus.previous}
                      onClick={handleLeftArrowClick}
                      {...hooks.hook(props.leftArrowAutomationHook)}
                    >
                      <LeftArrow
                        disabled={props.arrowsDisabledStatus.previous}
                      />
                    </ArrowButton>
                  </GAClickEventTracker>
                </Box>
              )}
              <Box
                d="flex"
                w={props.withNavigationArrows ? '90%' : '100%'}
                justifyContent="space-between"
                automationHook={props.tabsWrapperHook}
              >
                <TabsWrapper role="tablist">
                  {React.Children.map(props.children, (child, index) => {
                    const tab = React.cloneElement(child, {
                      tabIndex: index === currentTabIndex ? '0' : '-1',
                      tabRef: tabRefs[index],
                      id: `${
                        props.namespace ? props.namespace + '-' : ''
                      }Tab-${index}`,
                      active: index === currentTabIndex,
                      onClick: partial(activateTab, index, child, false),
                      contentId: `${
                        props.namespace ? props.namespace + '-' : ''
                      }Tab-Panel-${index}`,
                    });

                    return (
                      <TabWrapper
                        role="presentation"
                        children={tab}
                        active={index === currentTabIndex}
                      />
                    );
                  })}
                </TabsWrapper>
              </Box>
              {props.withNavigationArrows && (
                <Box
                  w="5%"
                  d="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <GAClickEventTracker
                    onClick={() => arrowClickTracker(props.rightArrowAriaLabel)}
                  >
                    <ArrowButton
                      type="button"
                      aria-label={props.rightArrowAriaLabel}
                      disabled={props.arrowsDisabledStatus.next}
                      onClick={handleRightArrowClick}
                      {...hooks.hook(props.rightArrowAutomationHook)}
                    >
                      <RightArrow disabled={props.arrowsDisabledStatus.next} />
                    </ArrowButton>
                  </GAClickEventTracker>
                </Box>
              )}
            </Box>
          </div>

          <div
            id={`${
              props.namespace ? props.namespace + '-' : ''
            }Tab-Panel-${currentTabIndex}`}
            role="tabpanel"
            tabIndex="0"
            aria-labelledby={`${
              props.namespace ? props.namespace + '-' : ''
            }Tab-${currentTabIndex}`}
          >
            {renderTabContents()}
          </div>
        </div>
      }
    />
  );
};

const KEY_LEFT_ARROW = 37;
const KEY_RIGHT_ARROW = 39;

const StyledTabs = styled(withTheme(Tabs))`
  position: relative;
`;

const ArrowButton = styled.button`
  width: 100%;
  height: 100%;
`;

const TabsWrapper = styled.ul`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
`;

const TabWrapper = styled.li`
  display: flex;
  flex: 1;

  &:last-child {
    border-right: 0;
  }
`;

Tabs.propTypes = propTypes;
Tabs.defaultProps = defaultProps;
StyledTabs.Tab = Tab;

const LeftArrow = withTheme(({ theme, disabled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="100%"
    height="100%"
    viewBox="0 0 45 45"
  >
    <path
      fill="none"
      fillRule="nonzero"
      stroke={
        disabled
          ? theme.colors.common.disabled
          : theme.colors.common.peacockBlue
      }
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M29 11L16 22.5 29 34"
    />
  </svg>
));

const RightArrow = withTheme(({ theme, disabled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="100%"
    height="100%"
    viewBox="0 0 45 45"
  >
    <path
      fill="none"
      fillRule="nonzero"
      stroke={
        disabled
          ? theme.colors.common.disabled
          : theme.colors.common.peacockBlue
      }
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M16 11l13 11.5L16 34"
    />
  </svg>
));

export default StyledTabs;
